import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { Json } from "@/integrations/supabase/types";

export interface GeneratedSchema {
  title: string;
  type: string;
  jsonLd: object | string;
  isNew?: boolean;
  isImproved?: boolean;
  originalSchema?: object; // Original schema for comparison when isImproved=true
}

export interface AnalysisHistoryItem {
  id: string;
  user_id: string;
  url: string;
  page_type: string | null;
  title: string | null;
  schemas_count: number;
  schemas_data: GeneratedSchema[] | null;
  project_id: string | null;
  created_at: string;
}

interface AddToHistoryInput {
  url: string;
  page_type: string | null;
  title: string | null;
  schemas_count: number;
  schemas_data?: GeneratedSchema[];
  project_id?: string;
}

export function useAnalysisHistory() {
  const { user } = useAuth();
  const [history, setHistory] = useState<AnalysisHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchHistory();
    } else {
      setHistory([]);
      setLoading(false);
    }
  }, [user]);

  const fetchHistory = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from("analysis_history")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Parse schemas_data from Json to GeneratedSchema[]
      const parsedData = (data || []).map(item => ({
        ...item,
        schemas_data: (Array.isArray(item.schemas_data) ? item.schemas_data : null) as unknown as GeneratedSchema[] | null
      }));
      
      setHistory(parsedData);
    } catch (error) {
      console.error("Error fetching history:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToHistory = async (item: AddToHistoryInput) => {
    if (!user) return { error: new Error("No user") };

    try {
      const insertData: Record<string, unknown> = {
        user_id: user.id,
        url: item.url,
        page_type: item.page_type,
        title: item.title,
        schemas_count: item.schemas_count,
        schemas_data: item.schemas_data as unknown as Json,
      };
      
      if (item.project_id) {
        insertData.project_id = item.project_id;
      }

      const { error } = await (supabase as any).from("analysis_history").insert(insertData);

      if (error) throw error;

      await fetchHistory();
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const deleteFromHistory = async (id: string) => {
    try {
      const { error } = await supabase.from("analysis_history").delete().eq("id", id);

      if (error) throw error;

      setHistory((prev) => prev.filter((item) => item.id !== id));
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const updateProjectLink = async (analysisId: string, projectId: string | null) => {
    try {
      const { error } = await (supabase as any)
        .from("analysis_history")
        .update({ project_id: projectId })
        .eq("id", analysisId);

      if (error) throw error;

      setHistory((prev) =>
        prev.map((item) =>
          item.id === analysisId ? { ...item, project_id: projectId } : item
        )
      );
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  return {
    history,
    loading,
    addToHistory,
    deleteFromHistory,
    updateProjectLink,
    refetch: fetchHistory,
  };
}
